import { FC, type ReactNode } from 'react';
import { Col, Row, Statistic } from 'antd';

import { WorkspaceUsage } from '../../types';

interface UsageOverviewStatsProps {
    usageData: WorkspaceUsage | null;
}

interface UsageOverviewMetric {
    key: keyof NonNullable<WorkspaceUsage['overview']>;
    label: string;
    precision?: number;
    suffix?: ReactNode;
}

const overviewMetrics: UsageOverviewMetric[] = [
    { key: 'activeUsers', label: 'Active Members' },
    { key: 'activeGuests', label: 'Active Guests' },
    { key: 'activeContributors', label: 'Active Contributors' },
    { key: 'contributorRate', label: 'Contributor Rate', precision: 1, suffix: '%' },
    { key: 'totalActivityEvents', label: 'Activity Events' },
    { key: 'activeDays', label: 'Active Days' },
    { key: 'resourcesTouched', label: 'Resources Touched' },
    { key: 'resourceTypesActive', label: 'Resource Types Active' },
    { key: 'resourcesCreated', label: 'Resources Created' },
    { key: 'resourcesUpdated', label: 'Resources Updated' },
    { key: 'resourcesDeleted', label: 'Resources Deleted' },
    { key: 'commentsAdded', label: 'Comments Added' },
    { key: 'collaborationChanges', label: 'Collaborator Changes' },
    { key: 'versionsCreated', label: 'Versions Created' },
    { key: 'exportsCompleted', label: 'Exports Completed' },
    { key: 'averageEventsPerResource', label: 'Events per Resource', precision: 1 },
];

const UsageOverviewStats: FC<UsageOverviewStatsProps> = ({ usageData }): JSX.Element => {
    const overview = usageData?.overview;

    return (
        <Row gutter={[16, 16]}>
            {overviewMetrics.map((metric) => {
                const legacyValue =
                    metric.key === 'activeUsers'
                        ? usageData?.activeUsers
                        : metric.key === 'activeGuests'
                          ? usageData?.activeGuests
                          : undefined;
                const rawValue = overview?.[metric.key] ?? legacyValue ?? 0;
                const value = metric.key === 'contributorRate' ? rawValue * 100 : rawValue;

                return (
                    <Col key={metric.key} xs={24} sm={12} lg={6}>
                        <Statistic
                            precision={metric.precision}
                            suffix={metric.suffix}
                            title={<small>{metric.label}</small>}
                            value={value}
                        />
                    </Col>
                );
            })}
        </Row>
    );
};

export default UsageOverviewStats;
